import { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * useRailFilters - Hook for managing rail navigation filters
 * Handles state, URL sync, and filter application
 * 
 * @param {Object} config - Configuration object
 * @param {string} config.sport - Current sport
 * @param {Object} config.defaultFilters - Default filter values
 * @param {Function} config.onFiltersChange - Callback when filters change
 */
const useRailFilters = ({ 
  sport = 'all',
  defaultFilters = {},
  onFiltersChange
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Parse URL params to initial state
  const getInitialFilters = useCallback(() => {
    const params = new URLSearchParams(location.search);
    const filters = { ...defaultFilters };

    for (const [key, value] of params.entries()) {
      // Handle comma-separated values for multi-select
      if (value.includes(',')) {
        filters[key] = value.split(',');
      } else {
        filters[key] = value;
      }
    }

    if (!params.has('sort') && defaultFilters.sort === 'smart') {
      filters.sort = 'smart';
    }


    return filters;
  }, [location.search, defaultFilters]); // eslint-disable-line react-hooks/exhaustive-deps
  
  const [filters, setFilters] = useState(getInitialFilters);
  const [previousFilters, setPreviousFilters] = useState(filters);
  
  // Sync filters to URL
  useEffect(() => {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== defaultFilters[key]) {
        if (Array.isArray(value)) {
          params.set(key, value.join(','));
        } else if (value) {
          params.set(key, value);
        }
      }
    });
    
    const newSearch = params.toString();
    const currentSearch = location.search.substring(1);
    
    if (newSearch !== currentSearch) {
      navigate({
        pathname: location.pathname,
        search: newSearch ? `?${newSearch}` : ''
      }, { replace: true });
    }
  }, [filters, defaultFilters, location.pathname, location.search, navigate]);
  
  // Notify parent of filter changes
  useEffect(() => {
    if (onFiltersChange && JSON.stringify(filters) !== JSON.stringify(previousFilters)) {
      // Debug: Log when filters change and notify parent component
      // console.log('📢 [useRailFilters] Notifying parent of filter change:', {
      //   prev: previousFilters,
      //   new: filters,
      //   changed: Object.keys(filters).filter(key => filters[key] !== previousFilters[key])
      // });
      onFiltersChange(filters);
      setPreviousFilters(filters);
    }
  }, [filters, previousFilters, onFiltersChange]);
  
  // Update single filter
  const updateFilter = useCallback((key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  }, []);
  
  // Reset all filters
  const resetFilters = useCallback(() => {
    setFilters(defaultFilters);
  }, [defaultFilters]);
  
  // Reset single filter
  const resetFilter = useCallback((key) => {
    setFilters(prev => ({
      ...prev,
      [key]: defaultFilters[key]
    }));
  }, [defaultFilters]);
  
  // Check if any filters are active (non-default)
  const hasActiveFilters = useMemo(() => {
    return Object.entries(filters).some(([key, value]) => {
      const defaultValue = defaultFilters[key];
      
      // Handle arrays
      if (Array.isArray(value) && Array.isArray(defaultValue)) {
        return JSON.stringify(value.sort()) !== JSON.stringify(defaultValue.sort());
      }
      
      // Handle objects (like league in baseball)
      if (typeof value === 'object' && value !== null && 
          typeof defaultValue === 'object' && defaultValue !== null) {
        return JSON.stringify(value) !== JSON.stringify(defaultValue);
      }
      
      // Handle primitives
      return value !== defaultValue;
    });
  }, [filters, defaultFilters]);
  
  // Get active filter count
  const activeFilterCount = useMemo(() => {
    return Object.entries(filters).filter(([key, value]) => {
      const defaultValue = defaultFilters[key];
      
      // Handle arrays
      if (Array.isArray(value) && Array.isArray(defaultValue)) {
        return JSON.stringify(value.sort()) !== JSON.stringify(defaultValue.sort());
      }
      
      // Handle objects (like league in baseball)
      if (typeof value === 'object' && value !== null && 
          typeof defaultValue === 'object' && defaultValue !== null) {
        return JSON.stringify(value) !== JSON.stringify(defaultValue);
      }
      
      // Handle primitives
      return value !== defaultValue;
    }).length;
  }, [filters, defaultFilters]);
  
  // Build filter objects for SecondaryRail
  const buildFilterObjects = useCallback((filterConfig) => {
    return filterConfig.map(config => {
      const value = filters[config.id];
      const defaultValue = defaultFilters[config.id];
      
      // Universal comparison that works for all data types
      let hasNonDefault = false;
      if (Array.isArray(value)) {
        hasNonDefault = JSON.stringify(value.sort()) !== JSON.stringify((defaultValue || []).sort());
      } else if (typeof value === 'object' && value !== null) {
        hasNonDefault = JSON.stringify(value) !== JSON.stringify(defaultValue);
      } else {
        hasNonDefault = value !== defaultValue;
      }
      
      return {
        ...config,
        value,
        hasNonDefault,
        label: config.getLabel ? config.getLabel(value) : config.label
      };
    });
  }, [filters, defaultFilters]);
  
  return {
    filters,
    updateFilter,
    resetFilters,
    resetFilter,
    hasActiveFilters,
    activeFilterCount,
    buildFilterObjects
  };
};

export default useRailFilters;